import React, { useState, forwardRef, useEffect } from "react";
import { FaceFun, FaceNeutral, FaceSad } from "@gravity-ui/icons";
import apiService from "../../api/apiService";
import Toast from "../generic_components/Toast";
import { usePostHog } from "posthog-js/react";
import { getCookie } from "../../utils/cookieUtils";
import axios from "axios";
import { useGameContext } from "../../contexts/GameContext"; 
import TaskCompletion from "./TaskCompletion";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";
// Convert to use forwardRef to accept a ref from parent
const FeedbackForm = forwardRef(({ closeModal }, ref) => {
  const {gamificationData , fetchGamificationData} = useGameContext();
  const posthog = usePostHog();
  const [feedback, setFeedback] = useState("");
  const [selectedMood, setSelectedMood] = useState(null);
  const isFormValid = feedback.trim() !== "";
  const [toastMessage, setToastMessage] = useState(null);
  const [triggerTaskModal, setTriggerTaskModal] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeModal]);
  

    const taskUpdate = async () => {
      try {
        const feedbackTask = gamificationData?.tasks?.find(task => task.task_key === "give_feedback");
        
        if (feedbackTask && !feedbackTask.is_completed) {
          console.log("Attempting to complete feedback task");
          const token = getCookie("token") || getPartitionedLocalStorage("token");
          
          const response = await axios.post(
            `${process.env.REACT_APP_SERVER_ENDPOINT}/api/complete-task`,
            { taskKey: "give_feedback" },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          
          console.log("Task update response:", response.data);
          
          if (response.data.status === "completed") {
            console.log("Feedback task completed successfully");
            setTriggerTaskModal(true);
            setTimeout(() => setTriggerTaskModal(false), 8000);
            await fetchGamificationData();
            return true;
          }
        } else {
          console.log("Feedback task already completed or not found", feedbackTask);
        }
        return false;
      } catch (error) {
        console.error("Error updating feedback task:", error);
        Sentry.captureException(error);
        return false;
      }
    }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.handleProtectedRoute("storeFeedback", {
        feedback,
        mood: selectedMood,
      });
      
      const taskUpdated = await taskUpdate();
      console.log("Task update result:", taskUpdated);
      
      setToastMessage("Thanks! You rock! 🤘");
      posthog.capture("User Feedback Submitted", {
        is_user_logged_in: true
      });
      
      setTimeout(() => {
        closeModal();
      }, taskUpdated ? 3000 : 1500);
    } catch (error) {
      console.error("Error submitting feedback:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to submit feedback. Please try again.");
    }
  };

  return (
    <div
      id="feedback-form"
      ref={ref} // Add the ref here to track this element
      className="md:w-[480px] w-[320px] bg-[#1D1B1D] border border-[#4E4A4F] rounded-lg text-white"
    >
      <div className="pb-0 pt-3 px-4">
        <h2 className="text-lg font-normal text-left">Got feedback?</h2>
      </div>
      <div className="px-4 pb-4">
        <p className="text-[#C5C3C6] text-sm font-extralight text-left pb-4">
          Your thoughts = our glow-up. What's good? What's meh? 💡✨
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <textarea
            placeholder="Your feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            className="w-full min-h-[160px] bg-transparent border border-[#878188] rounded-xl text-white text-sm font-extralight placeholder:text-[#878188] p-2 focus:outline-none appearance-none focus:ring-0"
          />
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {[
                { icon: FaceSad, mood: "sad" },
                { icon: FaceNeutral, mood: "neutral" },
                { icon: FaceFun, mood: "happy" },
              ].map(({ icon: Icon, mood }) => (
                <button
                  key={mood}
                  type="button"
                  className={`flex w-9 h-9 border border-[#4E4A4F] items-center justify-center rounded-full ${
                    selectedMood === mood ? "bg-[#4E4A4F]" : "bg-transparent"
                  } hover:bg-[#2D2B2D]`}
                  onClick={() => setSelectedMood(mood)}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      selectedMood === mood ? "text-white" : "text-[#878188]"
                    }`}
                  />
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={closeModal}
                className="px-3 h-9 text-sm font-normal text-[#878188] hover:text-white bg-white/5 hover:bg-white/10 rounded-full"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid}
                className={`px-3 h-9 text-sm font-normal rounded-full ${
                  isFormValid
                    ? "bg-white text-[#100F10] hover:bg-[#E0DFE1]"
                    : "bg-[#4E4A4F] text-[#878188] cursor-not-allowed"
                }`}
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
      {toastMessage && (
        <Toast
          type="success"
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
    {triggerTaskModal && (<TaskCompletion trigger={triggerTaskModal} />)}
    </div>
  );
});

export default FeedbackForm;
